import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import type { Producto } from '@/types';
import { useCartStore } from '@/stores/cartStore';
import { ProductImage } from './ProductImage';
import { PrecioDisplay } from '@/components/ui/PrecioDisplay';
import { productCartMeta, productPath } from '@/lib/catalog';
import { cn } from '@/lib/utils';

interface ProductCardProps {
  product: Producto;
  className?: string;
}

export function ProductCard({ product, className }: ProductCardProps) {
  const addItem = useCartStore((s) => s.addItem);
  const precio = product.precio_oferta ?? product.precio_venta;
  const sinStock = product.existencias <= 0;
  const path = productPath(product);
  const cartMeta = productCartMeta(product);

  return (
    <article className={cn('card-product group', className)}>
      <Link
        to={path}
        className="relative block aspect-[4/3] overflow-hidden border-b border-surface-100"
      >
        <ProductImage
          imagen={product.imagen}
          alt={product.nombre_pto}
          modelo={product.modelo_pto}
          fallbackLabel={product.nombre_pto}
          categoria={product.categoria}
          className="h-full w-full"
        />
        {product.precio_oferta && (
          <span className="badge-offer absolute left-2 top-2">Oferta</span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-3.5">
        <div className="flex items-start justify-between gap-2">
          <p className="font-mono text-[11px] text-surface-500">{product.modelo_pto}</p>
          <span className={cn('shrink-0', sinStock ? 'badge-out' : 'badge-stock')}>
            {sinStock ? '0' : product.existencias}
          </span>
        </div>
        <Link to={path}>
          <h3 className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-surface-900 hover:text-brand-700">
            {product.nombre_pto}
          </h3>
        </Link>
        {product.marca && (
          <p className="mt-0.5 text-xs text-surface-500">{product.marca}</p>
        )}

        <div className="mt-auto flex items-end justify-between gap-2 border-t border-surface-100 pt-3">
          <PrecioDisplay
            precio={precio}
            precioLista={product.precio_oferta ? product.precio_venta : null}
            variant="card"
          />
          <button
            type="button"
            disabled={sinStock}
            onClick={() =>
              addItem({
                tipo: 'producto',
                refId: cartMeta.refId,
                nombre: product.nombre_pto,
                imagen: product.imagen,
                precio,
                cantidad: 1,
                origen: cartMeta.origen,
                idExterno: cartMeta.idExterno,
                skuExterno: cartMeta.skuExterno,
              })
            }
            className="btn-primary shrink-0 px-3 py-2 text-xs disabled:opacity-40"
            aria-label="Agregar"
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            Agregar
          </button>
        </div>
      </div>
    </article>
  );
}

export { KitCard } from './KitCard';
